import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCrossTabCount } from 'hooks/useCrossTabCount';
import { EBroadcastChannel } from 'constants/enums';
import { OfferData } from 'constants/apiResponses.types';

import {
  clearShowing,
  getClosedOfferIdsThisSession,
  getCooldownEndsAtMilliseconds,
  isAnotherTabShowing,
  markDismissed,
  markOfferClosedThisSession,
  pickEligibleStoreRefreshOffer,
  writeShowingLock,
} from './useStoreRefresh.utils';
import { SHOWING_STALE_MILLISECONDS } from './useStoreRefresh.consts';

interface IUseStoreRefreshProps {
  offers: OfferData[];
}

interface IUseStoreRefreshReturn {
  count: number;
  eligibleOffer: OfferData | undefined;
  isOpen: boolean;
  /** Milliseconds when cooldown ends per offer, only when that offer is on cooldown. */
  cooldownEndsByOfferId: Record<string, number>;
  onCollect: (offerId: string) => void;
  onClose: (offerId: string) => void;
}

/**
 * Hook that manages store-refresh popup eligibility, cross-tab locking,
 * and dismiss handling for one or more store-refresh offers.
 *
 * @example
 * const {
 *   eligibleOffer,
 *   isOpen,
 *   cooldownEndsByOfferId,
 *   onCollect,
 *   onClose,
 * } = useStoreRefresh({ offers: storeRefreshOffers });
 */
export const useStoreRefresh = (
  props: IUseStoreRefreshProps,
): IUseStoreRefreshReturn => {
  const { offers } = props;

  const { count, reset } = useCrossTabCount({
    channelName: EBroadcastChannel.STORE_REFRESH_CHANNEL_NAME,
    storageKey: EBroadcastChannel.STORE_REFRESH_STORAGE_KEY,
    autoIncrement: offers.length > 0,
  });

  useEffect((): (() => void) => {
    return (): void => {
      clearShowing();
    };
  }, []);

  const eligibleOffer = useMemo(
    (): OfferData | undefined => pickEligibleStoreRefreshOffer(offers, count),
    [offers, count],
  );

  const [closedIds, setClosedIds] = useState<ReadonlySet<string>>(
    getClosedOfferIdsThisSession,
  );

  const isOpen = useMemo((): boolean => {
    if (!eligibleOffer) return false;
    if (closedIds.has(eligibleOffer.offerId)) return false;
    return !isAnotherTabShowing();
  }, [eligibleOffer, closedIds]);

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined;
    writeShowingLock();
    const interval = setInterval(writeShowingLock, SHOWING_STALE_MILLISECONDS / 2);
    return (): void => clearInterval(interval);
  }, [isOpen]);

  const cooldownEndsByOfferId = useMemo((): Record<string, number> => {
    const map: Record<string, number> = {};
    for (const offer of offers) {
      const endsAt = getCooldownEndsAtMilliseconds(offer);
      if (endsAt !== null) {
        map[offer.offerId] = endsAt;
      }
    }
    return map;
  }, [offers, count]);

  const onCollect = useCallback((offerId: string): void => {
    markDismissed(offerId);
    clearShowing();
    reset();
  }, [reset]);

  const onClose = useCallback((offerId: string): void => {
    setClosedIds(markOfferClosedThisSession(offerId));
    clearShowing();
  }, []);

  return {
    count,
    eligibleOffer,
    isOpen,
    cooldownEndsByOfferId,
    onCollect,
    onClose,
  };
};
